import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Divider
} from '@material-ui/core';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import EditIcon from '@material-ui/icons/Edit';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
    height: 'auto', // Allow the card to grow with content
  },
  noBorder: {
    borderBottom: 'none !important',
  },
  alert: {
    marginBottom: theme.spacing(4)
  },
  contentWrapper: {
    height: 'auto',
    overflowY: 'visible',
    paddingBottom: theme.spacing(4)
  },
  content: {
    width: '100%',
  },
  gridOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  customerAddress: {
    textTransform: 'uppercase',
    fontSize: '0.8em'
  },
  coveragesSummary: {
    position: 'sticky',
    top: 0,
    zIndex: 4,
    width: '100%',
    padding: theme.spacing(2),
    marginTop: theme.spacing(4),
    border: 0,
    borderRadius: theme.spacing(1),
    background: 'linear-gradient(rgba(150, 202, 255, 0.18), rgba(150, 242, 255, 0.18))',
  },
  paddingBox: {
    height: theme.spacing(5.75),
    backgroundColor: theme.palette.background.paper,
    marginLeft: -2,
    width: 'calc(100% + 1px)',
    position: 'sticky',
    zIndex: 1,
    top: 55,
  },

  claimDetailSectionHeader: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    '& h5,& h4': {
      fontWeight: 'bold',
    }
  },
  '@supports ((-webkit-backdrop-filter: initial) or (backdrop-filter: initial))': {
    coveragesSummary: {
      backdropFilter: 'blur(10px)',
    }
  },
  contentWrapperBox: {
    padding: '0 16px 16px'
  },
  noBorderTable: {
    '& .MuiTableCell-root': {
      borderBottom: 'none !important',
    }
  },
}));

const getCreatedDateLabel = (dateCreated) => {
  return dateCreated ? format(parseISO(dateCreated), 'M/d/y h:mm aaa') : '';
};

function Brief({ claimParts, claimNotes = [], isGlassProcessor, user, handleSaveNote, handleUpdateClaimNote, handleDeleteClaimNote }) {
  const classes = useStyles();
  const [newClaimNote, setNewClaimNote] = React.useState('');
  const [editingNoteId, setEditingNoteId] = React.useState(null);
  const [editingValue, setEditingValue] = React.useState('');
  const { t } = useTranslation();


  const startEdit = (noteId, note) => {
    setEditingNoteId(noteId);
    setEditingValue(note.claimNote);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingValue('');
  };

  const saveEditNote = (note) => {
    handleUpdateClaimNote({ ...note, claimNote: editingValue.trim() });
    setEditingNoteId(null);
    setEditingValue('');
  };

  return (<Box className={classes.contentWrapperBox}>
    <Grid container>
      <Grid item xs={12} className={clsx(classes.gridOverflow, classes.claimDetailSectionHeader)}>

        <Typography variant="h4" color="textSecondary" >
          {t('glassClaimOverview.partDetails')}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={8}>
        {claimParts && claimParts.length > 0 ? (
          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={6} sm={6}>
              <Typography variant="h6" color="textSecondary">
                {t('partNumber')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6}>
              <Typography variant="h6" color="textSecondary">
                {t('quantity')}
              </Typography>
            </Grid>
            {claimParts.map((part, noteId) => (
              <React.Fragment key={noteId}>
                <Grid item xs={6} sm={6}>
                  <Typography variant="h5" color="textPrimary" noWrap>
                    {part?.partNumber ?? ''}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Typography variant="h5" color="textPrimary" noWrap>
                    {part?.quantity ?? ''}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t('glassClaimOverview.noPartDetails')}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} className={clsx(classes.gridOverflow, classes.claimDetailSectionHeader)}
        style={{ marginTop: 32 }}>
        <Typography variant="h4" color="textSecondary">
          {t('notes')}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        {claimNotes && claimNotes.length > 0 ? (
          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={12} sm={2}>
              <Typography variant="subtitle1" color="textSecondary">
                {t('glassClaimNotes.date')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" color="textSecondary">
                {t('glassClaimNotes.user')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" color="textSecondary">
                {t('glassClaimNotes.note')}
              </Typography>
            </Grid>
            {claimNotes.map((note, noteId) => (
              <React.Fragment key={noteId}>
                <Grid item xs={12} sm={2}>
                  <Typography variant="h6" color="textPrimary" noWrap>
                    {getCreatedDateLabel(note.dateCreated)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="h6" color="textPrimary" noWrap>
                    {note.createdBy}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    {editingNoteId === noteId ? (
                      <>
                        <TextField
                          value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          size="small"
                          variant="outlined"
                          fullWidth
                        />
                        <Button
                          color="primary"
                          onClick={() => saveEditNote(note)}
                          style={{ marginLeft: 8 }}
                        >
                          {t('glassClaimActions.save')}
                        </Button>
                        <Button
                          color="secondary"
                          onClick={cancelEdit}
                          style={{ marginLeft: 8 }}
                        >
                          {t('glassClaimActions.cancel')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" color="textPrimary" style={{ flex: 1 }}>
                          {note.claimNote}
                        </Typography>
                        {isGlassProcessor && note.createdBy === user?.email && (
                          <Box>
                            <EditIcon
                              style={{ cursor: 'pointer', marginLeft: 8 }}
                              color="primary"
                              fontSize="small"
                              onClick={() => startEdit(noteId, note)}
                            />
                            <CancelOutlinedIcon
                              style={{ cursor: 'pointer', marginLeft: 8, color: '#3cb6e7' }}
                              color="error"
                              fontSize="small"
                              onClick={() => handleDeleteClaimNote(note)}
                            />
                          </Box>
                        )}

                      </>
                    )}
                  </Box>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t('glassClaimNotes.none')}
          </Typography>
        )}
      </Grid>
      {isGlassProcessor && (
        <>
          <Grid item xs={12} style={{ marginTop: 24, marginBottom: 12 }} >
            <Typography variant="h5" color="textSecondary" style={{ fontWeight: 'bold' }}>
              {t('glassClaimNotes.addNote')}
            </Typography>
          </Grid>

          <Grid item xs={12} mt>
            <TextField
              fullWidth
              multiline
              rows={4}
              name='newClaimNote'
              type='text'
              label={t('notes')}
              value={newClaimNote}
              onChange={(e) => setNewClaimNote(e.target.value)}
              variant="outlined"

            />
          </Grid>
          <Grid item xs={12} style={{ marginTop: '16px' }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setNewClaimNote('')}
                disabled={!newClaimNote}
              >
                {t('glassClaimActions.clear')}
              </Button>
              <Box ml={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    handleSaveNote(newClaimNote);
                    setNewClaimNote('');
                  }}
                  disabled={!newClaimNote.trim()}
                >
                  {t('glassClaimNotes.saveNote')}
                </Button>
              </Box>
            </Box>
          </Grid>
        </>
      )}


    </Grid>
    {/* <Box component="section" className={classes.root}>
      <Box className={classes.contentWrapper}>   </Box>
    </Box > */}
  </Box >
  );
}

Brief.propTypes = {
  claimParts: PropTypes.arrayOf(PropTypes.shape({
    partNumber: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  claimNotes: PropTypes.arrayOf(PropTypes.shape({
    dateCreated: PropTypes.string,
    createdBy: PropTypes.string,
    claimNote: PropTypes.string,
    vendorClaimNoteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  isGlassProcessor: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
  handleSaveNote: PropTypes.func.isRequired,
  handleUpdateClaimNote: PropTypes.func.isRequired,
  handleDeleteClaimNote: PropTypes.func.isRequired,
};

Brief.defaultProps = {
  claimParts: [],
  claimNotes: [],
  isGlassProcessor: false,
  user: undefined,
};

export default Brief;
